import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, X, Sparkles } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Tutorial tips for the demo.
 *
 * Routes are matched in order; the FIRST entry whose `match(loc)` returns
 * true wins. Each tip is intentionally short, friendly, and points the user
 * at something they should actually click next.
 * -------------------------------------------------------------------------*/
const TIPS = [
  {
    id: "landing",
    match: (loc) => loc.pathname === "/demo",
    label: "Start here",
    title: "Try the product as one of two creatives",
    body: "Pick a persona to see exactly what they would see signed in - their feed, their profile, and a job opening tailored to them.",
    next: "Pick Diego or Sam to begin.",
  },
  {
    id: "dashboard",
    match: (loc) => loc.pathname.startsWith("/demo/dashboard"),
    label: "Step 1 of 4 - The Daily Feed",
    title: "Two ranked job matches",
    body: "This is the feed they would open every morning. Match score is how well they fit the posting. Enjoyment is whether they would actually want it.",
    next: "Open the Profile tab next to see their raw, unedited info - then come back and click a job to watch it get rewritten.",
  },
  {
    id: "profile",
    match: (loc) => loc.pathname.startsWith("/demo/profile"),
    label: "Step 2 of 4 - The raw pool",
    title: "Their full creative life, dumped in one place",
    body: "Every job title, every portfolio piece, every case study they have ever written - it all lives here. Note how broad it is: the AI will not show all of it for every job.",
    next: "Now click 'Feed' in the top nav and open one of the jobs. Watch which parts of this profile actually surface.",
  },
  {
    id: "application",
    match: (loc) => loc.pathname.startsWith("/demo/applications/") && !loc.pathname.endsWith("/ats"),
    label: "Step 3 of 4 - Per-job tailoring",
    title: "Same person. Rewritten for this posting.",
    body: "The amber highlights are AI-tailored content. Hover any highlighted piece, resume bullet, or paragraph to see what got pulled from the profile for this specific job - and notice which portfolio pieces got dropped.",
    next: "Scroll down past the materials, then hit 'Run ATS Simulation' to see what this lift does to the score.",
  },
  {
    id: "ats",
    match: (loc) => loc.pathname.endsWith("/ats"),
    label: "Step 4 of 4 - The score lift",
    title: "Same resume. New ranking.",
    body: "We are running the as-is resume and the Sisyphean-tailored resume against the same ATS scoring model. The score jump is what gets a real human to actually read it.",
    next: "When the reveal finishes, that is the whole loop. Start your free trial to run this on your own postings.",
  },
];


const TutorialContext = createContext(null);

export function DemoTutorialProvider({ children }) {
  // Re-shows every demo visit per user request. Visit lives only in
  // sessionStorage; clearing the tab resets it.
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Reset dismiss state any time the tab is reopened (not within-tab nav).
  useEffect(() => {
    try {
      const k = "sisyphean-demo-tutorial-dismissed-session";
      const ses = sessionStorage.getItem(k);
      if (ses === "1") {
        setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  const dismiss = useCallback(() => {
    try { sessionStorage.setItem("sisyphean-demo-tutorial-dismissed-session", "1"); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  const restart = useCallback(() => {
    try { sessionStorage.removeItem("sisyphean-demo-tutorial-dismissed-session"); } catch { /* ignore */ }
    setDismissed(false);
    setMinimized(false);
  }, []);

  const value = useMemo(() => ({ dismissed, dismiss, restart, minimized, setMinimized }), [dismissed, dismiss, restart, minimized]);
  return (
    <TutorialContext.Provider value={value}>
      {children}
      <DemoTutorialCard />
    </TutorialContext.Provider>
  );
}

export function useDemoTutorial() {
  return useContext(TutorialContext) || { dismissed: true, dismiss: () => {}, restart: () => {}, minimized: false, setMinimized: () => {} };
}


function DemoTutorialCard() {
  const loc = useLocation();
  const { dismissed, dismiss, minimized, setMinimized } = useDemoTutorial();
  const tip = TIPS.find((t) => t.match(loc));
  if (!tip || dismissed) return null;

  // Minimized: tiny pill button (re-expand).
  if (minimized) {
    return (
      <button
        data-testid="demo-tutorial-reopen"
        onClick={() => setMinimized(false)}
        className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 bg-primary text-primary-foreground px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-mono shadow-xl hover:opacity-90 transition-opacity"
      >
        <Sparkles className="w-3 h-3" />
        Show guide
      </button>
    );
  }

  return (
    <div
      data-testid="demo-tutorial-card"
      className="fixed bottom-5 left-5 z-40 w-[330px] max-w-[calc(100vw-2.5rem)] panel p-4 shadow-2xl"
      style={{ borderColor: "hsl(var(--primary) / 0.45)" }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-primary" strokeWidth={2} />
          <span className="terminal-label text-primary text-[9px]">{tip.label}</span>
        </div>
        <div className="flex items-center gap-1 -mr-1 -mt-1">
          <button
            onClick={() => setMinimized(true)}
            title="Minimize"
            data-testid="demo-tutorial-minimize"
            className="text-muted-foreground hover:text-foreground p-1 text-[10px] font-mono"
          >_</button>
          <button
            onClick={dismiss}
            title="Close tour"
            data-testid="demo-tutorial-close"
            className="text-muted-foreground hover:text-foreground p-1"
          ><X className="w-3 h-3" /></button>
        </div>
      </div>
      <h4 className="font-display text-sm uppercase font-semibold tracking-tight mb-2 leading-snug">{tip.title}</h4>
      <p className="text-xs text-foreground/80 leading-relaxed mb-3">{tip.body}</p>
      <div className="border-t border-foreground/15 pt-3 flex items-start gap-2">
        <ChevronRight className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-foreground/70 leading-relaxed font-mono">{tip.next}</p>
      </div>
    </div>
  );
}
